import { retrieveState, StorageTypes } from '../../utils/persistentState';
import { VarName } from '../../utils/varNames';
import { ActionTypes, ActionType } from '../actionTypes';
import { SortType, HoursSelection, UISettingsState, ThemeMode } from '../types';

let initialDashboardPanels: VarName[] = [];
let initialActivePlotVars: VarName[] = [];
let initialActiveMarker: VarName = VarName.TemperatureC;
let initialShowHelp = false;
let initialSortBy: SortType = { property: 'name', ascending: true };
let initialSelectedHours: HoursSelection = {
  selectHours: true,
  startHour: 9,
  endHour: 17,
  includeWeekends: false,
};
let initialMotionThreshold: [number, number] = [5, 9]; // set default value to 30 mins
let initialThemeMode: ThemeMode = ThemeMode.dark;
const initialGhostParam = { addGhosts: false, weekCount: 1 };

try {
  initialDashboardPanels = (retrieveState(StorageTypes.DashboardPanels) as VarName[]) ?? [];
  initialActivePlotVars = (retrieveState(StorageTypes.PlotVars) as VarName[]) ?? [];
  initialActiveMarker =
    (retrieveState(StorageTypes.ActiveMarker) as VarName) ?? VarName.TemperatureC;
  initialShowHelp = retrieveState(StorageTypes.ShowingHelp) === 'true';
  initialSortBy = (retrieveState(StorageTypes.SortBy) as SortType) ?? initialSortBy;
  initialSelectedHours = (retrieveState(StorageTypes.SelectedHours) as HoursSelection) ?? {
    selectHours: false,
    startHour: 9,
    endHour: 17,
    includeWeekends: false,
  };
  initialMotionThreshold = (retrieveState(StorageTypes.MotionThreshold) as [number, number]) ?? [
    5, 9,
  ];
  initialThemeMode = (retrieveState(StorageTypes.ThemeType) as ThemeMode) ?? ThemeMode.dark;
} catch (err) {
  // console.warn(`Error attempting to initialise the UI state ${err}`);
}

const initialState: UISettingsState = {
  highlightedItem: {
    id: '',
  },
  activeMarker: initialActiveMarker,
  clickedItem: { id: '' },
  dashboardPanels: initialDashboardPanels,
  activePlotVars: initialActivePlotVars,
  showHelp: initialShowHelp,
  searchTerm: { term: '', source: undefined },
  sortBy: initialSortBy,
  selectedHours: initialSelectedHours,
  motionThreshold: initialMotionThreshold,
  showSensorLabels: false,
  showTaggedSpaces: true,
  allowBleLocSwitch: true,
  bleWebViewUrl: '',
  bleScanStatus: false,
  showNavDrawer: false,
  themeMode: initialThemeMode,
  ghostParams: initialGhostParam,
  showStackedTraces: true, // available mainly for occumancy(Wifi/BLE) and energy traces
  showPlotGrid: true,
  showPlotLegend: false,
  showPlotHighlight: true,
  selectedBand: null,
  showAdvancedAnalysis: false,
  availableVars: [],
};

// eslint-disable-next-line default-param-last
export default function setUISettings(state = initialState, action: ActionTypes): UISettingsState {
  switch (action.type) {
    case ActionType.SET_MOTION_THRESHOLD: {
      const motionThreshold = action.payload;
      return {
        ...state,
        motionThreshold,
      };
    }
    case ActionType.SET_SELECTED_HOURS: {
      const selectedHours = action.payload;
      return {
        ...state,
        selectedHours,
      };
    }
    case ActionType.SET_SORT_BY: {
      const sortBy = action.payload;
      return {
        ...state,
        sortBy,
      };
    }
    case ActionType.SET_SEARCH_TERM: {
      const {
        payload: { term, source },
      } = action;
      // We only start searching when 3 or more characters are typed
      const searchTerm =
        term.length > 2 ? { term: term.toLowerCase(), source } : { term: '', source: undefined };

      return {
        ...state,
        searchTerm,
      };
    }
    case ActionType.SHOW_HELP: {
      const showHelp = action.payload;
      return {
        ...state,
        showHelp,
      };
    }
    case ActionType.TOGGLE_ACTIVE_PLOT_VAR: {
      const varName = action.payload;
      const newVars = state.activePlotVars;
      const index = newVars.indexOf(varName);
      if (index === -1) {
        newVars.push(varName);
      } else {
        newVars.splice(index, 1);
      }

      return {
        ...state,
        activePlotVars: [...newVars],
      };
    }
    case ActionType.SET_DASHBOARD_PANEL: {
      const panel = action.payload;
      const newPanels = [panel];

      return {
        ...state,
        dashboardPanels: [...newPanels],
      };
    }
    case ActionType.TOGGLE_DASHBOARD_PANEL: {
      const panel = action.payload;
      const newPanels = state.dashboardPanels;
      const index = newPanels.indexOf(panel);
      if (index === -1) {
        newPanels.push(panel);
      } else {
        newPanels.splice(index, 1);
      }

      return {
        ...state,
        dashboardPanels: [...newPanels],
      };
    }
    case ActionType.SET_CLICKED_ITEM: {
      const clickedItem = action.payload;
      return {
        ...state,
        clickedItem,
      };
    }
    case ActionType.SET_ACTIVE_MARKER: {
      const activeMarker = action.payload;
      return {
        ...state,
        activeMarker,
      };
    }
    case ActionType.SET_HIGHLIGHTED_ITEM: {
      const highlightedItemId = action.payload;
      return {
        ...state,
        highlightedItem: highlightedItemId,
      };
    }
    case ActionType.TOGGLE_SHOW_SENSOR_LABELS: {
      const showSensorLabelsCurrent = state.showSensorLabels;
      return {
        ...state,
        showSensorLabels: !showSensorLabelsCurrent,
      };
    }
    case ActionType.TOGGLE_SHOW_TAGGED_SPACES: {
      const showTaggedSpacesCurrent = state.showTaggedSpaces;
      return {
        ...state,
        showTaggedSpaces: !showTaggedSpacesCurrent,
      };
    }
    case ActionType.ALLOW_BLE_LOC_SWITCH: {
      const allowBleLocSwitch = action.payload;
      return {
        ...state,
        allowBleLocSwitch,
      };
    }
    case ActionType.SET_BLE_WEBVIEW_URL: {
      const url = action.payload;
      return {
        ...state,
        bleWebViewUrl: url,
      };
    }
    case ActionType.SET_BLE_SCAN_STATUS: {
      const status = action.payload;
      return {
        ...state,
        bleScanStatus: status,
      };
    }
    case ActionType.SET_SHOW_NAV_DRAWER: {
      const showNavDrawer = action.payload;
      return {
        ...state,
        showNavDrawer,
      };
    }
    case ActionType.SET_THEME_MODE: {
      const themeMode = action.payload;
      return {
        ...state,
        themeMode,
      };
    }
    case ActionType.SET_GHOST_PARAMS: {
      const ghostParams = action.payload;
      return {
        ...state,
        ghostParams,
      };
    }
    case ActionType.SET_STACKED_TRACES: {
      const stackedTraces = action.payload;
      return {
        ...state,
        showStackedTraces: stackedTraces,
      };
    }
    case ActionType.SET_PLOT_GRID: {
      const showPlotGrid = action.payload;
      return {
        ...state,
        showPlotGrid,
      };
    }
    case ActionType.SET_PLOT_LEGEND: {
      const showPlotLegend = action.payload;
      return {
        ...state,
        showPlotLegend,
      };
    }
    case ActionType.SET_PLOT_HIGHLIGHT: {
      const showPlotHighlight = action.payload;
      return {
        ...state,
        showPlotHighlight,
      };
    }
    case ActionType.SET_SELECTED_BAND: {
      const selectedBand = action.payload;
      return {
        ...state,
        selectedBand,
      };
    }
    case ActionType.TOGGLE_SHOW_ADVANCED_ANALYSIS: {
      const showAdvancedAnalysis = action.payload;
      return {
        ...state,
        showAdvancedAnalysis,
      };
    }
    case ActionType.SET_AVAILABLE_VARS: {
      const availableVars = action.payload;
      return {
        ...state,
        availableVars,
      };
    }
    default: {
      return state;
    }
  }
}
